import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  FormControl,
  Grid2,
  IconButton,
  MenuItem,
  Modal,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import accountService from "../api/services/account-service";
import coreScheduleService from "../api/services/core-schedule-service";
import UploadHistoryService from "../api/services/upload-history";
import UploadHistoryTable from "../components/upload-history-table-antd";
import { showErrorToast, showSuccessToast, showWarningToast } from "../helpers/utils";
import { ReactComponent as FileIconActive } from "../Icons/vuesax/document-text-active.svg";
import { ReactComponent as FileUploadIconActive } from "../Icons/vuesax/document-upload-active.svg";
import { ReactComponent as FileUploadIconDisabled } from "../Icons/vuesax/document-upload-disabled.svg";
import { ReactComponent as ImportIconActive } from "../Icons/vuesax/export-active.svg";
import { ReactComponent as ImportIconDisabled } from "../Icons/vuesax/export-disabled.svg";
import unauth from "../images/unauth.png";
import { pca } from "../msalConfig";
import { PermissionsContext } from "../permission-context";

function CoreScheduleImporter() {
  const { loading, error, hasPermission } = useContext(PermissionsContext);
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("");
  const [filePath, setFilePath] = useState("");
  const [fileSelected, setFileSelected] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [disableSave, setDisableSave] = useState(true);
  const [disableSelectFile, setDisableSelectFile] = useState(true);
  const [disableCloseButton, setDisableCloseButton] = useState(false);

  // CSV line numbers are 1-based and include the header line. The header is not validated.
  const findInvalidRows = async (file) => {
    const contents = await file.text();
    const lines = contents.replace(/^\uFEFF/, "").split(/\r?\n/);
    const invalidRows = [];

    for (let index = 1; index < lines.length; index += 1) {
      // Ignore an optional trailing blank line. Any non-ASCII character in a data row is invalid.
      if (lines[index].trim() && Array.from(lines[index]).some((character) => character.codePointAt(0) > 0x7F)) {
        invalidRows.push(index + 1);
      }
    }

    return invalidRows;
  };

  const showInvalidRowsToast = (rows) => {
    toast.error(
      `Unsupported special characters found in CSV row${rows.length === 1 ? "" : "s"}: ${rows.join(", ")}. Fix these rows and re-upload. Row numbers are CSV line numbers, including the header as line 1.`,
      {
        position: "top-right",
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: true,
        theme: "dark",
      }
    );
  };

  const user = pca && pca.getActiveAccount() ? pca.getActiveAccount().username : "";

  const handleClose = () => {
    setOpen(false);
    setDisableSave(true);
    setDisableSelectFile(true);
    setAccount("");
    setFilePath("");
    setFileSelected(null);
  };
  const handleModal = () => {
    setOpen(true);
  };
  const handleChange = (event) => {
    setAccount(event.target.value);
    setDisableSelectFile(false);
  };

  const fetchData = async () => {
    try {
      const res = await accountService.getAccounts();
      if (res.status === 200 || res.status === 201) {
        setAccounts(res.data || ["No Accounts available"]);
      } else {
        showErrorToast(
          "Unable to fetch accounts. Please check your connection or try again later."
        );
        setAccounts([]);
      }
    } catch (error) {
      showErrorToast(
        "An unexpected error occurred while loading user accounts. Please try again later."
      );
      setAccounts([]);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await UploadHistoryService.getHistory(2);
      if (res.status === 200 || res.status === 201) {
        setHistoryData(res.data);
      } else {
        showErrorToast(
          "Unable to fetch accounts. Please check your connection or try again later."
        );
        setHistoryData([]);
      }
    } catch (error) {
      showErrorToast("Error fetching Upload History:", error);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    // Allow selecting the same file again after correcting it.
    event.target.value = "";
    if (!file) return;

    setFilePath(file.name);
    setFileSelected(null);
    setDisableSave(true);

    try {
      const invalidRows = await findInvalidRows(file);
      if (invalidRows.length) {
        showInvalidRowsToast(invalidRows);
        return;
      }
      setFileSelected(file);
      setDisableSave(false);
    } catch (error) {
      toast.error("Unable to read this CSV file. Please select it again.", {
        position: "top-right",
        autoClose: false,
        closeButton: true,
        theme: "dark",
      });
    }
  };

  const handleSubmit = async () => {
    if (!fileSelected) {
      showErrorToast("Please select a valid CSV file before importing.");
      return;
    }

    // Revalidate immediately before upload so invalid data never reaches the backend.
    let invalidRows;
    try {
      invalidRows = await findInvalidRows(fileSelected);
    } catch (error) {
      toast.error("Unable to read this CSV file. Please select it again.", {
        position: "top-right",
        autoClose: false,
        closeButton: true,
        theme: "dark",
      });
      return;
    }
    if (invalidRows.length) {
      setFileSelected(null);
      setDisableSave(true);
      showInvalidRowsToast(invalidRows);
      return;
    }

    setDisableSave(true);
    setDisableSelectFile(true);
    setDisableCloseButton(true);
    const formData = new FormData();
    formData.append("file", fileSelected);
    formData.append("AccountId", account);
    formData.append("UserId", user);

    try {
      var result = await coreScheduleService.uploadCoreSchedule(formData);
      var message = result.data["Upload Details"];
      if (result.data["Upload Status"] === "2") {
        showWarningToast("Partially Uploaded. " + message);
      } else if (result.data["Upload Status"] === "1") {
        showSuccessToast("File Uploaded Successfully");
      }
    } catch (error) {
      showErrorToast("Upload Failed: " + error);
    } finally {
      setDisableSave(false);
      setDisableSelectFile(false);
      setHistoryData(null);
      setDisableCloseButton(false);
      fetchHistory();
    }
  };
  useEffect(() => {
    fetchHistory();
  }, []);
  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  return (
    <>
      {!loading && !error && hasPermission("PatientVisits") && (
        <Box style={{ marginRight: "1%", marginLeft: "2%" }}>
          <Grid2
            container
            justifyContent='space-between'
            alignItems='center'
            sx={{
              borderBottom: 1,
              borderColor: "#dfdfdf",
              marginTop: 1.5,
            }}>
            <Grid2>
              <Typography
                gutterBottom
                fontWeight='700'
                lineHeight={"31.25px"}
                fontSize={"24px"}
                fontStyle={"DM Sans"}>
                CoreSchedule Importer
              </Typography>
            </Grid2>
            <Grid2
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                transform: "translateY(-4px)",
              }}>
              <Button
                variant='contained'
                component='span'
                startIcon={
                  <FileUploadIconActive width='1em' height='1em' style={{ fontSize: "22px" }} />
                }
                onClick={handleModal}>
                Upload CSV
              </Button>
            </Grid2>
          </Grid2>
          <Grid2 size={12}>
            <UploadHistoryTable data={historyData} tableName='coreScheduleImporter' />
          </Grid2>
          <Grid2
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Modal
              open={open}
              onClose={handleClose}
              aria-labelledby='modal-modal-title'
              aria-describedby='modal-modal-description'>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  width: "80%",
                  maxWidth: 600,
                  height: "auto",
                  borderRadius: "12px",
                  bgcolor: "white",
                  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
                  padding: 2,
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  "@media (max-width:600px)": {
                    width: "90%",
                    padding: 1,
                  },
                }}>
                <Tooltip title='Close'>
                  <IconButton
                    aria-label='close'
                    disabled={disableCloseButton}
                    onClick={handleClose}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 10,
                    }}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>

                <Grid2 container sx={{ height: "100%" }}>
                  <Grid2
                    container
                    size={12}
                    justifyContent='center'
                    alignItems='center'
                    sx={{ marginRight: "4px", padding: "31px" }}>
                    <FormControl
                      sx={{
                        m: 1,
                        minWidth: "100%",
                        maxWidth: 300,
                        marginLeft: 2,
                      }}
                      size='small'>
                      <Select
                        labelId='account'
                        id='account'
                        name='account'
                        value={account}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={
                          account !== ""
                            ? undefined
                            : () => (
                                <div
                                  style={{
                                    color: "#777777",
                                    fontFamily: "DM Sans",
                                    fontWeight: 400,
                                    size: "12px",
                                  }}>
                                  Account
                                </div>
                              )
                        }
                        sx={{ borderRadius: "12px" }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                            },
                          },
                        }}>
                        {accounts?.map((accountValue, index) => (
                          <MenuItem
                            key={index}
                            value={accountValue}
                            sx={{
                              font: "DM Sans",
                              lineHeight: "15.62px",
                              color: "#777777",
                              fontWeight: "400",
                            }}>
                            {accountValue}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2
                    container
                    size={12}
                    justifyContent='space-evenly'
                    alignItems='center'
                    spacing={4}>
                    <Grid2 xs={12} sm={6}>
                      <TextField
                        id='outlined-multiline-flexible'
                        placeholder='File'
                        maxRows={4}
                        value={filePath.length > 30 ? `${filePath.substring(0, 30)}...` : filePath}
                        InputProps={{
                          readOnly: true,
                        }}
                        title={filePath}
                        sx={{
                          width: "100%",
                          maxWidth: 400,
                          minWidth: 300,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "10px 14px",
                            marginBottom: "1px",
                          },
                          "@media (max-width:600px)": {
                            minWidth: 200,
                          },
                        }}
                      />
                    </Grid2>

                    <Grid2 xs={12} sm={6}>
                      <input
                        accept='.csv'
                        id='file-upload'
                        type='file'
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        disabled={disableSelectFile}
                      />
                      <label htmlFor='file-upload'>
                        <Button
                          variant='contained'
                          color='primary'
                          component='span'
                          type='submit'
                          startIcon={
                            disableSelectFile ? (
                              <FileUploadIconDisabled width='1em' height='1em' />
                            ) : (
                              <FileIconActive width='1em' height='1em' />
                            )
                          }
                          disabled={disableSelectFile}
                          fullWidth>
                          Select File
                        </Button>
                      </label>
                    </Grid2>
                  </Grid2>

                  <Grid2
                    container
                    size={12}
                    p={4}
                    justifyContent='space-evenly'
                    alignItems='center'>
                    <Button
                      variant='contained'
                      color='primary'
                      component='span'
                      startIcon={
                        disableSelectFile || disableSave ? (
                          <ImportIconDisabled width='1em' height='1em' />
                        ) : (
                          <ImportIconActive width='1em' height='1em' />
                        )
                      }
                      onClick={handleSubmit}
                      disabled={disableSelectFile || disableSave}
                      fullWidth>
                      Import
                    </Button>
                  </Grid2>
                </Grid2>
              </Box>
            </Modal>
          </Grid2>
        </Box>
      )}
      {!loading && !error && !hasPermission("PatientVisits") && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "90vh",
          }}>
          <img src={unauth} alt='UnAuthorized' style={{ maxWidth: "100%", maxHeight: "100%" }} />
        </Box>
      )}
    </>
  );
}

export default CoreScheduleImporter;
